import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  Stack,
  Button,
  Text,
  Group,
  Alert,
  Center,
  AspectRatio,
  ActionIcon,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertTriangle, IconCheck, IconCopy } from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";
import { CopyButton } from "@mantine/core";
import { Bob } from "../../stores/Bob";

const ShowBobNostrConnection = observer(({ bob }: { bob: Bob }) => {
  const connection = bob.selectedNostrConnection;
  const [showQRCode, setShowQRCode] = useState(
    connection?.kind === "receive_only"
  );

  /*  Detect "tiny" screens (≤ 320 px wide). JioPhone 2 is 240 px.  */
  const isTinyScreen = useMediaQuery("(max-width: 320px)");

  /*  QR size: 300 px everywhere except tiny screens (shrink to 200 px).  */
  const qrSize = isTinyScreen ? 200 : 300;

  if (!connection) {
    return null;
  }

  return (
    <Stack gap="md">
      <Center>
        <Text>
          <strong>{connection.name}</strong>
        </Text>
      </Center>

      {!showQRCode ? (
        <>
          <Alert
            icon={<IconAlertTriangle />}
            title="Warning"
            color="red"
            radius="md"
          >
            Don’t reveal this QR or connection code to anyone, except for apps
            and websites that you trust. Anyone who has it could spend funds
            from your vault!
          </Alert>

          <Button
            variant="outline"
            color="blue"
            onClick={() => setShowQRCode(true)}
          >
            OK, show me the code
          </Button>
        </>
      ) : (
        <>
          <Center>
            <AspectRatio ratio={1} style={{ maxWidth: qrSize }}>
              <QRCodeSVG
                value={connection.nwc_uri}
                size={qrSize}
                bgColor="#ffffff"
                marginSize={6}
                fgColor="#000000"
                level="L"
                title="NWC Connection Code"
              />
            </AspectRatio>
          </Center>
          <Center>
            <Group gap="xs" align="center">
              <CopyButton value={connection.nwc_uri}>
                {({ copied, copy }) => (
                  <Button
                    color={copied ? "teal" : "blue"}
                    onClick={copy}
                    leftSection={
                      copied ? <IconCheck size={16} /> : <IconCopy size={16} />
                    }
                  >
                    {copied ? "Copied!" : "Copy Connection Code"}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Center>
          {connection.kind === "receive_only" && (
            <Alert color="green">
              This NWC connection code can be only be used to receive funds.
              It's safe to use with untrusted apps.
            </Alert>
          )}
          {connection.kind === "send_and_receive" && (
            <Alert color="red">
              Anyone who sees this QR code or connection code can spend funds
              from your vault! Only share it with apps you trust.
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
});

export default ShowBobNostrConnection;
